// Core & Types
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import type { 
    AnalysisOrPlanResult, 
    SrmProject,
    Page, 
    NavParams, 
    IntegratedAnalysisResult, 
    EpThreatAnalysisResult, 
    SrmModule 
} from '@types';

// Constants & Context
import { ICONS, RISK_LEVEL_COLORS } from './constants';
import { db, functions } from '../../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import { collection, doc, getDoc, onSnapshot } from 'firebase/firestore';

// Shared UI & Infrastructure
import useGoogleMaps from './useGoogleMaps'; 
import { getEventRiskScore } from '../utils/riskEventFeed';
import { cleanReportPromptText } from '../../functions/src/common/reportPromptText';
import { useAnalysisPersistence } from '../hooks/useAnalysisPersistence';
import { redactDailyMicroReport } from '../utils/dailyMicroRedaction';
import { normalizeReportData } from '../utils/reportDataMerge';
import { reportDocumentLocation } from '../utils/reportDocumentLocation';
import { countryReportTimeZone } from '../../functions/src/common/countryReportTime';
import { EP_SURVEY_REPORT_TYPE, epSurveyReportRequestId } from '../../functions/src/common/epSurveyReport';
import EpSurveyPackageDisplay from './reports/EpSurveyPackageDisplay';

// Type Guards
import { 
    isCrisisManagementPlanResult,
    isCrisisManagementFrameworkResult,
    isBusinessResiliencyPlanResult,
    isBusinessResiliencyFrameworkResult,
    isEmergencyPreparednessFrameworkResult,
    isEmergencyPreparednessPlanResult,
    isLocalIncidentManagementResult,
    isRouteAnalysisResult,
    isEnterpriseFsatResult,
    isFSATReport,
    isEnterpriseAuditResult,
    isIntegratedAnalysisResult,
    isEventSecurityPlanResult,
    isEpThreatAnalysisResult,
    isStaffProtectionPlanResult,
    isTravelSecurityBriefResult,
    isHSSEBriefing,
    isHSSEIncident,
    isEpOpsResult,
    isGlobalThreatGroupAnalysisResult,
    isFacilityProtectionResult,
    isItineraryRiskResult,
    isCompositeReportResult,
    isAuditRemediationResult,
    isDailyMicroReport,
    isGeopoliticalAnalysis,
    isGeopoliticalAnalysisResult,
    isSrmBlueprint,
    isSrmAnalysis,
    isTextResult,
    isCapstoneFeedbackResult,
    isEpAdvanceReport,
    isEpPIBriefResult
} from '../utils/analysisTypeGuards';

// Modular Report Components
import AnalysisReportShell from './reports/AnalysisReportShell';
import CompositeReportDisplay from './reports/CompositeReportDisplay';
import { 
    CrisisManagementDisplay, 
    BusinessResiliencyDisplay, 
    EmergencyPreparednessDisplay,
    LocalIncidentManagementDisplay
} from './reports/ResiliencyReports';
import { EnterpriseAuditResultDisplay } from './reports/EnterpriseAuditResultDisplay';
import EnterpriseFsatResultDisplay from './reports/EnterpriseFsatResultDisplay';
import IntegratedAnalysisDisplay from './reports/IntegratedAnalysisDisplay';
import AuditRemediationDisplay from './reports/AuditRemediationDisplay';
import EpThreatAnalysisContent from './reports/EpThreatAnalysisContent';
import StaffProtectionPlanDisplay from './reports/StaffProtectionPlanDisplay';
import EmergencyPreparednessFrameworkDisplay from './reports/EmergencyPreparednessFrameworkDisplay';
import BusinessResiliencyFrameworkDisplay from './reports/BusinessResiliencyFrameworkDisplay';
import CrisisManagementFrameworkDisplay from './reports/CrisisManagementFrameworkDisplay';
import EventSecurityPlanDisplay from './reports/EventSecurityPlanDisplay';
import FacilityProtectionDisplay from './reports/FacilityProtectionDisplay';
import TravelSecurityBriefDisplay from './reports/TravelSecurityBriefDisplay';
import ItineraryRiskDisplay from './reports/ItineraryRiskDisplay';
import GeopoliticalAnalysisDisplay from './reports/GeopoliticalAnalysisDisplay';
import FSATReportDisplay from './reports/FSATReportDisplay';
import TextResultContent from './reports/TextResultContent';
import HsseBriefingDisplay from './reports/HsseBriefingDisplay';
import EpOpsAnalysisDisplay from './reports/EpOpsAnalysisDisplay';
import DailyMicroReportDisplay from './reports/DailyMicroReportDisplay';
import IntelligenceAnalysisDisplay from './reports/IntelligenceAnalysisDisplay';
import { INTELLIGENCE_REPORT_TYPE } from '../../functions/src/common/intelligenceAnalysis';
import DailyAreaReportDisplay from './reports/DailyAreaReportDisplay';
import GlobalThreatGroupDisplay from './reports/GlobalThreatGroupDisplay';
import RouteAnalysisDisplay from './reports/RouteAnalysisDisplay';
import SrmAnalysisDisplay from './reports/SrmAnalysisDisplay';
import SrmBlueprintDisplay from './reports/SrmBlueprintDisplay';
import CapstoneFeedbackDisplay from './reports/CapstoneFeedbackDisplay';
import ThreatGroupBriefing from './ThreatGroupBriefing';
import EpAdvanceSurveyDisplay from './reports/EpAdvanceSurveyDisplay';
import EpPIBriefDisplay from './reports/EpPIBriefDisplay';

// Shared UI Building Blocks
import { 
    sanitizeFirestoreData 
} from './reports/ReportShared';
import { reportSaveScope, type ReportSaveScope } from '../common/analysisPersistence';

interface ReportRegistryEntry {
    guard: (result: any) => boolean;
    component: React.ComponentType<any>;
    title: string;
    subtitle: string;
}

/**
 * Mapping of workflow names to their canonical reportType IDs.
 * A mapping of internal workflow IDs to their corresponding high-fidelity report types.
 * This is used as a fallback when the AI returns a generic report type (like 'GeminiResponse')
 * but we know it belongs to a specific workflow that has a premium display component.
 */
const REPORT_TYPE_MAP: Record<string, string> = {
    // ── Geopolitical ──────────────────────────────────────────────────────────
    'gg': 'GeopoliticalAnalysis',
    'country_geopolitical_analysis': 'GeopoliticalAnalysis',
    'state_of_the_country_analysis': 'GeopoliticalAnalysis',
    'Generate Country Analysis': 'GeopoliticalAnalysis',
    'Comprehensive Country Geopolitical & Risk Analysis': 'GeopoliticalAnalysis',
    // Deep Dive / High-Fidelity manual trigger workflows
    'macroDeepDive': 'GeopoliticalAnalysis',
    'macro_hf': 'GeopoliticalAnalysis',
    'daily_micro_report': 'DailyMicroReport',

    // ── Executive Protection ──────────────────────────────────────────────────
    'integrated_threat_analysis': 'IntegratedAnalysisResult',
    'Integrated Threat Analysis': 'IntegratedAnalysisResult',
    'Integrated Intelligence': 'IntegratedAnalysisResult',
    'ep_threat_analysis': 'EpThreatAnalysisResult',
    'ep_ops_risk_analysis': 'EpOpsAnalysisResult',
    // H2: Advanced route analysis routes to the full RouteAnalysisResult component
    'advanced_route_analysis_ep_plan': 'RouteAnalysisResult',

    // ── Security Risk Management (SRM) ────────────────────────────────────────
    'facility_risk_assessment': 'EnterpriseRiskAudit',
    'facility_asset_protection': 'FacilityProtectionResult',
    // H2: Staff protection plan returns EpThreatAnalysisResult from the backend;
    // must match the enforceResultParity stamp in geminiService_impl.ts
    'staff_protection_plan': 'EpThreatAnalysisResult',
    // H2: Project planner output is an SRM Blueprint
    'project_planner': 'SrmBlueprint',
    // H2: Event security plan
    'event_security_plan': 'EventSecurityPlanResult',

    // ── Travel & Route ────────────────────────────────────────────────────────
    'travel_security_brief': 'TravelSecurityBriefResult',
    'itinerary_risk_workflow': 'ItineraryRiskResult',

    // ── HSSE ─────────────────────────────────────────────────────────────────
    // H2 FIX: 'hsse_suite_link' removed — it is a navigation shortcut handled by
    // handleWorkflowSelect → onNavigateToSuite() and never reaches the AI pipeline.
    'compliance_audit': 'HSSEComplianceBriefing',
    'hsse_compliance_briefing': 'HSSEComplianceBriefing',
    // Incident report has its own type
    'hsse_incident_report': 'HSSEIncidentReport',

    // ── Crisis & Resiliency (CMP) ─────────────────────────────────────────────
    // These three CMP workflows have protected routing via dedicated backend services
    'crisis_management_plan': 'CrisisManagementPlanResult',
    'business_resiliency_framework': 'BusinessResiliencyPlanResult',
    'emergency_preparedness_plan': 'EmergencyPreparednessPlanResult',

    // ── EP Operational Workflows (Hybrid Integration) ─────────────────────────
    // These two workflows use custom form UIs but route results through the standard
    // pipeline (WorkflowDashboard → AnalysisResultDisplay → AnalysisReportShell).
    'ep_advance_site_survey': 'EpAdvanceReport',
    'ep_pi_brief': 'EpPIBriefResult',
};

/**
 * Registry of report components and their associated metadata.
 * This decouples the dispatch logic from the main component.
 */
const REPORT_COMPONENTS_REGISTRY: ReportRegistryEntry[] = [
    {
        guard: report => report?.reportType === INTELLIGENCE_REPORT_TYPE,
        component: IntelligenceAnalysisDisplay,
        title: 'Intelligence Assessment',
        subtitle: 'Source verification, business impact, and recommended actions',
    },
    {
        guard: report => report?.reportType === 'DailyMicroReport' && Array.isArray(report?.reportArea?.subdivisionCodes) && Array.isArray(report?.localEvents),
        component: DailyAreaReportDisplay,
        title: 'Daily Micro Report',
        subtitle: 'Local events and national context',
    },
    {
        guard: isEpOpsResult,
        component: EpOpsAnalysisDisplay,
        title: "Security & EP Operations Risk Analysis",
        subtitle: "Tactical Intelligence Dashboard"
    },
    {
        guard: isGlobalThreatGroupAnalysisResult,
        component: GlobalThreatGroupDisplay,
        title: "Global Threat Group Intelligence",
        subtitle: "State & Non-State Actor Analysis"
    },
    {
        guard: isCrisisManagementFrameworkResult,
        component: CrisisManagementFrameworkDisplay,
        title: "Crisis Management Plan",
        subtitle: "S-13 · Consultant-approved crisis response plan"
    },
    {
        guard: isCrisisManagementPlanResult,
        component: CrisisManagementDisplay,
        title: "Crisis Management Plan",
        subtitle: "Business Resiliency Module • Insights Generated Plan"
    },
    {
        guard: isBusinessResiliencyPlanResult,
        component: BusinessResiliencyDisplay,
        title: "Business Resiliency Framework",
        subtitle: "Business Resiliency Module • Insights Generated Plan"
    },
    {
        guard: isBusinessResiliencyFrameworkResult,
        component: BusinessResiliencyFrameworkDisplay,
        title: "Business Resiliency Framework",
        subtitle: "S-14 · Consultant-approved continuity plan"
    },
    {
        guard: isEmergencyPreparednessFrameworkResult,
        component: EmergencyPreparednessFrameworkDisplay,
        title: 'Emergency Preparedness Plan',
        subtitle: 'S-16 · Consultant-approved emergency plan'
    },
    {
        guard: isEmergencyPreparednessPlanResult,
        component: EmergencyPreparednessDisplay,
        title: "Emergency Preparedness Protocol",
        subtitle: "Business Resiliency Module • Insights Generated Plan"
    },
    {
        guard: isLocalIncidentManagementResult,
        component: LocalIncidentManagementDisplay,
        title: "Local Incident Management",
        subtitle: "Field Operations • Crisis Response Framework"
    },
    {
        guard: isEnterpriseAuditResult,
        component: EnterpriseAuditResultDisplay,
        title: "Enterprise Audit Intelligence",
        subtitle: "Global Compliance & Remediation Analysis"
    },
    {
        guard: isEnterpriseFsatResult,
        component: EnterpriseFsatResultDisplay,
        title: "Holistic FSAT Sweep Report",
        subtitle: "Global Scan • Assets Aggregated"
    },
    {
        guard: isSrmAnalysis,
        component: SrmAnalysisDisplay,
        title: "Security Risk Management Analysis",
        subtitle: "Operational Asset Protection Framework"
    },
    {
        guard: isIntegratedAnalysisResult,
        component: IntegratedAnalysisDisplay,
        title: "Strategic Security Intelligence",
        subtitle: "Multi-Vector Risk Assessment"
    },
    {
        guard: isAuditRemediationResult,
        component: AuditRemediationDisplay,
        title: "Audit Remediation Strategy",
        subtitle: "Compliance Recovery & Hardening Plan"
    },
    {
        guard: isEpThreatAnalysisResult,
        component: EpThreatAnalysisContent,
        title: "Executive Protection Threat Analysis",
        subtitle: "Principal Security & Vector Assessment"
    },
    {
        guard: isStaffProtectionPlanResult,
        component: StaffProtectionPlanDisplay,
        title: "Staff Protection Plan",
        subtitle: "S-03 · Consultant-approved personnel protection"
    },
    {
        guard: isEventSecurityPlanResult,
        component: EventSecurityPlanDisplay,
        title: "Tactical Event Security Plan",
        subtitle: "Operation Order • SEC-OPS A1"
    },
    {
        guard: isFacilityProtectionResult,
        component: FacilityProtectionDisplay,
        title: "Facility Protection Strategy",
        subtitle: "Asset Hardening & Site Security Analysis"
    },
    {
        guard: isTravelSecurityBriefResult,
        component: TravelSecurityBriefDisplay,
        title: "Travel Security Briefing",
        subtitle: "Personnel Protection & Regional Risks"
    },
    {
        guard: isItineraryRiskResult,
        component: ItineraryRiskDisplay,
        title: "Itinerary Risk Assessment",
        subtitle: "Travel Intelligence • Pathfinding Security"
    },
    {
        guard: isGeopoliticalAnalysisResult,
        component: GeopoliticalAnalysisDisplay,
        title: "Comprehensive Geopolitical Intelligence Dossier",
        subtitle: "Strategic Regional Analysis & Forecasting"
    },
    {
        guard: isGeopoliticalAnalysis,
        component: GeopoliticalAnalysisDisplay,
        title: "Geopolitical Intelligence Dossier",
        subtitle: "Strategic Regional Analysis & Forecasting"
    },
    {
        guard: isFSATReport,
        component: FSATReportDisplay,
        title: "Facility Security Assessment",
        subtitle: "Physical Security Audit & Compliance"
    },
    {
        guard: isHSSEBriefing,
        component: HsseBriefingDisplay,
        title: "HSSE Compliance Briefing",
        subtitle: "Regulatory \u0026 Operational Safety Standards"
    },
    {
        // I4 FIX: Wire HSSE Incident Reports to ThreatGroupBriefing, which contains
        // HSSEIncidentContent — previously the guard was missing from this registry.
        guard: isHSSEIncident,
        component: ThreatGroupBriefing,
        title: "HSSE Incident Report",
        subtitle: "Incident Analysis \u0026 Corrective Action Framework"
    },
    {
        // BOUNDARY: DailyMicroReport must appear AFTER GeopoliticalAnalysis guards.
        // Daily Micro reports share the GeopoliticalAnalysisDisplay component but are
        // strictly gated by reportType = 'DailyMicroReport' | 'CountryRiskProfile'.
        // The comprehensive dossier (reportType = 'GeopoliticalAnalysis') must win first.
        guard: isDailyMicroReport,
        component: GeopoliticalAnalysisDisplay,
        title: "Daily Country Analysis",
        subtitle: "High-Fidelity Strategic Intelligence"
    },
    {
        guard: isRouteAnalysisResult,
        component: RouteAnalysisDisplay,
        title: "Tactical Route Analysis",
        subtitle: "Travel Security • Movement Risk Assessment"
    },
    {
        guard: isSrmBlueprint,
        component: SrmBlueprintDisplay,
        title: "Strategic Security Architecture",
        subtitle: "ISO 31000 Alignment • SRM Framework"
    },
    {
        guard: isCapstoneFeedbackResult,
        component: CapstoneFeedbackDisplay,
        title: "Assessment Feedback",
        subtitle: "Capstone Scenario Performance Review"
    },
    {
        guard: report => report?.reportType === EP_SURVEY_REPORT_TYPE && report?.documents,
        component: EpSurveyPackageDisplay,
        title: "Advance Site Survey",
        subtitle: "Consultant-reviewed executive protection package"
    },
    {
        guard: isEpAdvanceReport,
        component: EpAdvanceSurveyDisplay,
        title: "Advance Agent Site Survey",
        subtitle: "USSS-Standard Advance Report · EP Intelligence"
    },
    {
        guard: isEpPIBriefResult,
        component: EpPIBriefDisplay,
        title: "Pre-Movement Intelligence Brief",
        subtitle: "Protective Intelligence Brief · EP Operations"
    },
    {
        guard: isCompositeReportResult,
        component: CompositeReportDisplay,
        title: "Composite Security Intelligence",
        subtitle: "Multi-Source Aggregated Intelligence"
    }
];

export interface AnalysisResultDisplayProps {
    result: AnalysisOrPlanResult;
    reportData?: any;
    country?: string;
    alerts?: any[];
    sources?: any[];
    onSave?: (data: any) => void;
    isSaving?: boolean;
    onNavigate?: (nav: { page: Page; params?: NavParams }) => void;
    onCustomSave?: () => Promise<void>;
    customSaveLabel?: string;
    onRegenerate?: (context: { deliverySnapshot?: boolean; serviceRequestId?: string;
        reportType?: string; country?: string; reportArea?: any }) => void;
    onConvertAnalysisToProject?: (result: EpThreatAnalysisResult) => Promise<void>;
    srmProject?: SrmProject;
    srmModule?: SrmModule;
    onSaveToFramework?: (...args: any[]) => Promise<void> | void;
    readOnly?: boolean;
    onStartFsatAudit?: (planData: any) => void;
    onLaunchChecklist?: (checklistName: string) => void;
    isDailyMicro?: boolean;
    reportId?: string;
    reportDate?: string;
    clientId?: string;
    organizationId?: string;
    activeWorkflow?: string;
    // G5 FIX: Back-navigation from report view to suite workflow list
    onBack?: () => void;
    backLabel?: string;
}

/**
 * Orchestrator component for displaying various intelligence reports.
 * Uses a registry-driven pattern to dispatch rendering to specialized components.
 */
export const AnalysisResultDisplay: React.FC<AnalysisResultDisplayProps> = ({ 
    result,
    reportData,
    country,
    alerts,
    onNavigate,
    onCustomSave,
    customSaveLabel,
    onRegenerate,
    onConvertAnalysisToProject,
    srmProject,
    srmModule,
    onStartFsatAudit,
    onLaunchChecklist,
    isDailyMicro,
    reportId: propReportId,
    reportDate,
    clientId,
    organizationId,
    onSaveToFramework,
    readOnly = false,
    activeWorkflow: propActiveWorkflow,
    // G5 FIX
    onBack,
    backLabel = 'Back to Suite',
}) => {
    const { isLoaded } = useGoogleMaps();
    const [isAutoFetching, setIsAutoFetching] = useState(false);
    const [autoFetchData, setAutoFetchData] = useState<any>(null);
    const [confidenceReport, setConfidenceReport] = useState<any>(null);
    const [refreshingConfidence, setRefreshingConfidence] = useState(false);
    const [confidenceRefreshError, setConfidenceRefreshError] = useState('');
    const confidenceRefreshVersion = useRef(0);
    const [savedDailyEvents, setSavedDailyEvents] = useState<any[]>([]);
    const [savedReportContext, setSavedReportContext] = useState<({ deliverySnapshot: boolean; serviceRequestId?: string } & ReportSaveScope) | null>(null);
    const [fetchError, setFetchError] = useState('');
    const location = useLocation();

    // Extract reportId from navigation state
    const reportId = (location.state as any)?.reportId || (location.state as any)?.reportData?.reportId;

    // Restore reports from durable URL fields after a fresh page load.
    useEffect(() => {
        let active = true;
        const fetchHistoricalReport = async () => {
            const targetId = propReportId || reportId || (result as any)?.reportId;
            const needsFetch = !reportData && (targetId || (isDailyMicro && !result));
            setAutoFetchData(null);
            setConfidenceReport(null);
            setConfidenceRefreshError('');
            confidenceRefreshVersion.current++;
            setRefreshingConfidence(false);
            setSavedReportContext(null);
            setFetchError('');
            setIsAutoFetching(false);
            if (!needsFetch)
                return;
            const source = reportDocumentLocation(targetId, country, isDailyMicro, reportDate);
            if (!source) {
                setFetchError('This report link has no valid report location.');
                return;
            }
            setIsAutoFetching(true);
            try {
                const surveyRequestId = epSurveyReportRequestId(targetId || '');
                if (surveyRequestId) {
                    const response = await httpsCallable(functions, 'manageServiceRequests')({ action: 'surveyPublishedReport',
                        organizationId: organizationId || srmProject?.organizationId || '', requestId: surveyRequestId });
                    if (active) {
                        setAutoFetchData((response.data as any).report);
                        setSavedReportContext({ deliverySnapshot: true, serviceRequestId: surveyRequestId });
                    }
                    return;
                }
                const snap = await getDoc(doc(db, source.path[0], ...source.path.slice(1)));
                if (!snap.exists() || (source.dailyMicro && snap.data().status !== 'success'))
                    throw new Error('The saved report is unavailable.');
                const data = snap.data();
                if (active) {
                    setAutoFetchData(source.dailyMicro ? data : data.resultData || data.reportData || data);
                    setSavedReportContext(source.dailyMicro ? null : {
                        ...reportSaveScope(data),
                        deliverySnapshot: data.deliverySnapshot === true,
                        serviceRequestId: data.serviceRequestId
                    });
                }
            } catch (error) {
                console.error('Report fetch failed:', error);
                if (active)
                    setFetchError('The saved report could not be loaded.');
            } finally {
                if (active)
                    setIsAutoFetching(false);
            }
        };
        void fetchHistoricalReport();
        return () => { active = false; confidenceRefreshVersion.current++; };
    }, [reportId, propReportId, isDailyMicro, reportData, result, country, reportDate, organizationId, srmProject?.organizationId]);

    // Normalize and sanitize report data
    const finalReport = useMemo(() => {
        const originalReport = sanitizeFirestoreData(confidenceReport || reportData || autoFetchData || result);
        const raw = normalizeReportData(originalReport);
        
        // Extract activeWorkflow from prop or URL if available
        const queryParams = new URLSearchParams(location.search);
        const activeWorkflow = propActiveWorkflow || reportSaveScope(raw).workflowId || srmModule?.workflowId || queryParams.get('workflow');
        const countryFromUrl = queryParams.get('country');

        let merged = raw;

        // 3. Apply reportType fix-up based on workflow registry if missing or generic
        // This ensures that reports generated via specific workflows route to their high-fidelity components
        if (activeWorkflow && (!merged?.reportType || merged?.reportType === 'GeminiResponse' || merged?.reportType === 'TextAnalysisResult')) {
            const mappedType = REPORT_TYPE_MAP[activeWorkflow];
            if (mappedType) {
                console.log(`[AnalysisResultDisplay] ✅ Mapping workflow "${activeWorkflow}" to reportType "${mappedType}"`);
                merged = { ...merged, reportType: mappedType };
            } else {
                console.warn(`[AnalysisResultDisplay] ⚠️ No mapping found for workflow "${activeWorkflow}"`);
            }
        }

        // Handle the case where the result is actually an object with a 'result' property (Proxy format)
        if (merged && merged.result && typeof merged.result === 'object' && !merged.reportType) {
            merged = { ...merged.result };
        }

        // Scavenge sections if they are missing but present as top-level keys
        const scavenged = Object.entries(merged || {}).filter(([key, value]) => {
            if (!value || typeof value !== 'string' || value.length < 50) return false;
            const handledKeys = [
                'summary', 'sections', 'pmesii', 'riskfactors', 'threatgroups', 
                'recommendedsuites', 'traveladvisory', 'reporttype', 'type', 'id',
                'analysis_date', 'confidencescore', 'infrastructuremapdata', 'keythreatgroups',
                'disclaimer', 'geospatialthreats', 'events', 'embassies', 'content',
                'tacticalsummary', 'executivesummary', 'threatassessment', 'ideology',
                'historicalcontext', 'advisorysummary', 'risksummary', 'reportsummary',
                'overallgeopoliticalriskscore', 'country', 'additionalSections',
                'incidentCommandSystem', 'tacticalResponseProtocols', 'crisisCommunicationMatrix',
                'demobilizationAndRecovery', 'recommendedChecklists', 'criticalFunctions',
                'supplyChainVulnerabilities', 'businessImpactAnalysis', 'recoveryStrategies',
                'incidentResponsePlan', 'evacuationRoutes', 'rallyPoints', 'emergencyResources',
                'tacticalChecklists', 'equipmentRequirements', 'incidentType', 'severityLevel',
                'roleChecklists', 'communicationProtocol', 'criticalResources', 'affectedLocation'
            ];
            return !handledKeys.includes(key.toLowerCase());
        }).map(([key, value]) => ({
            title: key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim().replace(/^\w/, c => c.toUpperCase()),
            content: value as string
        }));

        // 4a. COMPREHENSIVE GEOPOLITICAL HEURISTIC: If the data has canonical comprehensive
        // fields (pmesii, threatGroups) but no explicit reportType, stamp it as GeopoliticalAnalysis
        // BEFORE the isDailyMicro override can fire. This prevents Daily Micro from hijacking
        // a comprehensive dossier that was returned without an explicit reportType header.
        if (
            !merged?.reportType ||
            merged?.reportType === 'GeminiResponse' ||
            merged?.reportType === 'TextAnalysisResult'
        ) {
            const hasComprehensiveFields = !!merged?.pmesii || !!merged?.threatGroups || !!merged?.analystConfidence || !!merged?.predictiveAnalysis;
            if (hasComprehensiveFields) {
                console.log('[AnalysisResultDisplay] 🎯 Heuristic: Comprehensive geopolitical fields detected — stamping GeopoliticalAnalysis');
                merged = { ...merged, reportType: 'GeopoliticalAnalysis' };
            }
        }

        // 4b-pre. If the Cloud Function stamped _isDeepDive:true on the payload, it is
        // definitively a comprehensive Deep Dive — force GeopoliticalAnalysis regardless of
        // any nested unwrapping that may have downgraded the type to CountryRiskProfile.
        if (merged?._isDeepDive === true && merged?.reportType !== 'GeopoliticalAnalysis') {
            console.log('[AnalysisResultDisplay] 🎯 _isDeepDive flag detected — forcing GeopoliticalAnalysis');
            merged = { ...merged, reportType: 'GeopoliticalAnalysis' };
        }

        // 4b. Force DailyMicroReport type if the isDailyMicro prop is set AND this is NOT
        // already identified as a comprehensive GeopoliticalAnalysis report.
        // This guard prevents the Daily Micro prop from overwriting a comprehensive dossier.
        if (isDailyMicro && merged?.reportType !== 'GeopoliticalAnalysis' && (!merged?.reportType || merged?.reportType === 'GeminiResponse' || merged?.reportType === 'TextAnalysisResult' || merged?.reportType === 'DailyMicroReport' || merged?.reportType === 'CountryRiskProfile')) {
            console.log('[AnalysisResultDisplay] 🚀 Forcing DailyMicroReport type via prop');
            merged = { ...merged, reportType: 'DailyMicroReport' };
        }

        console.log(`[AnalysisResultDisplay] 🔍 Normalizing report. Workflow: ${activeWorkflow}, Final Type: ${merged?.reportType}, Data Keys:`, Object.keys(merged || {}));

        // Guard: if merged is null/undefined (fetch still in flight), return null so the
        // loading spinner at line 539 handles it rather than crashing here.
        if (!merged || Object.keys(merged).length === 0) return null;

        const finalSummary = merged?.summary || merged?.advisorySummary || merged?.executiveSummary || merged?.tacticalSummary || merged?.reportSummary || merged?.riskSummary || '';

        const additionalSections = [
            ...((!isCompositeReportResult(merged) && merged?.sections) || []),
            ...scavenged
        ].map(s => ({
            ...s,
            content: typeof s.content === 'string' ? s.content : JSON.stringify(s.content, null, 2)
        }));

        return cleanReportPromptText({
            ...merged,
            ...reportSaveScope(originalReport),
            ...reportSaveScope(savedReportContext),
            ...(activeWorkflow ? { workflowId: activeWorkflow } : {}),
            summary: finalSummary,
            additionalSections,
            ...(merged?.reportArea ? {} : { riskScore: getEventRiskScore(merged) ?? merged?.riskScore ?? merged?.overallGeopoliticalRiskScore }),
        });
    }, [confidenceReport, autoFetchData, reportData, result, propActiveWorkflow, srmModule?.workflowId, location.search, isDailyMicro, savedReportContext]);

    useEffect(() => {
        setSavedDailyEvents([]);
        if (!finalReport || !isDailyMicroReport(finalReport) || finalReport.reportArea ||
            !finalReport.iso3 || !finalReport.reportDate)
            return;
        return onSnapshot(collection(db, 'daily_country_analysis', finalReport.iso3, 'reports',
            finalReport.reportDate, 'events'), snapshot => {
            setSavedDailyEvents(snapshot.docs.map(event => event.data()));
        }, error => console.error('Saved daily events unavailable:', error));
    }, [finalReport?.iso3, finalReport?.reportDate, finalReport?.reportType, finalReport?.reportArea]);

    if (fetchError && !reportData && !autoFetchData)
        return <div role="alert" className="p-8 text-center text-sm font-semibold text-red-700">{fetchError}</div>;

    if (!finalReport || (isAutoFetching && !autoFetchData)) {
        return (
            <div className="flex items-center justify-center min-h-[60vh] bg-slate-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
                    <p className="text-slate-500">Initializing Intelligence Report...</p>
                </div>
            </div>
        );
    }

    // Error guard: if the payload is an error object, show a clean message instead of
    // falling through to TextResultContent which would render raw JSON.stringify output.
    const isErrorPayload = finalReport?.error || finalReport?.success === false || finalReport?.reportType === 'Error';
    if (isErrorPayload) {
        const errorMsg = typeof finalReport?.error === 'string'
            ? finalReport.error
            : finalReport?.message || finalReport?.summary || 'The analysis engine encountered an error. Please return to the dashboard and try again.';
        return (
            <div role="alert" className="flex items-center justify-center min-h-[60vh] bg-slate-50 p-8">
                <div className="text-center max-w-lg">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                    </div>
                    <h3 className="text-xl font-bold text-slate-800 mb-2">{finalReport.errorCode === 'empty_report' ? 'Report Not Generated' : 'Analysis Engine Error'}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed mb-6">{errorMsg}</p>
                    {onNavigate && (
                        <button
                            onClick={() => onNavigate({ page: 'gsocDashboard' })}
                            className="px-6 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors shadow-sm"
                        >
                            Return to Threat Analysis Hub
                        </button>
                    )}
                </div>
            </div>
        );
    }

    const refreshConfidence = async () => {
        const targetId = propReportId || reportId || (result as any)?.reportId;
        if (!targetId || refreshingConfidence)
            return;
        setRefreshingConfidence(true);
        setConfidenceRefreshError('');
        const version = confidenceRefreshVersion.current;
        try {
            const refresh = httpsCallable(functions, 'refreshFacilityReportConfidence', { timeout: 540000 });
            const response: any = await refresh({ reportId: targetId });
            if (version === confidenceRefreshVersion.current)
                setConfidenceReport(response.data.result);
        } catch (error: any) {
            if (version === confidenceRefreshVersion.current)
                setConfidenceRefreshError(error.message || 'Confidence could not be refreshed. Retry later.');
        } finally {
            if (version === confidenceRefreshVersion.current)
                setRefreshingConfidence(false);
        }
    };

    // Registry Dispatch
    const matchedEntry = REPORT_COMPONENTS_REGISTRY.find(entry => entry.guard(finalReport));
    const savedS02Plan = savedReportContext?.serviceRequestId &&
        (propReportId || reportId) === `service-${savedReportContext.serviceRequestId}-s02`;
    const regenerate = onRegenerate && (!savedReportContext?.deliverySnapshot ||
        (finalReport.reportType === 'FacilityProtectionResult' && savedS02Plan))
        ? () => onRegenerate({ ...savedReportContext, reportType: finalReport.reportType,
            country: finalReport.country || country, reportArea: finalReport.reportArea })
        : undefined;
    const persistenceProps = {
        organizationId: organizationId || srmProject?.organizationId || finalReport.organizationId || '',
        clientId: clientId || srmProject?.clientId || finalReport.clientId || null,
        srmProject,
        srmModule,
        preferredWorkflowId: propActiveWorkflow || finalReport.workflowId || srmModule?.workflowId,
    };

    if (matchedEntry) {
        const { component: Component } = matchedEntry;
        const dailyMicro = isDailyMicroReport(finalReport);
        let reportTimeZone = finalReport.timeZone;
        if (dailyMicro && !finalReport.reportArea && !reportTimeZone && finalReport.iso3)
            try {
                reportTimeZone = countryReportTimeZone(finalReport.iso3);
            } catch { /* Older reports may have unknown country codes. */ }
        const displayReport = dailyMicro ? redactDailyMicroReport({ ...finalReport,
            timeZone: reportTimeZone,
            dailyEvents: [...savedDailyEvents, ...(finalReport.dailyEvents || [])] }) : finalReport;
        const displayAlerts = dailyMicro ? [] : alerts;

        // M3 FIX: Override title for DailyMicro reports with the country name from the payload
        // so that re-opened saved reports show the correct contextual header instead of the
        // generic registry title "Daily Country Analysis".
        let { title, subtitle } = matchedEntry;
        if (finalReport?.reportType === INTELLIGENCE_REPORT_TYPE)
            title = finalReport.title;
        if (finalReport?.reportType === EP_SURVEY_REPORT_TYPE)
            title = `Advance Site Survey · ${finalReport.reference}`;
        if (isDailyMicro || finalReport?.reportType === 'DailyMicroReport') {
            const countryName = finalReport?.country || country || finalReport?.countryName || finalReport?.location;
            title = countryName ? `${countryName} — Daily Intelligence Brief` : 'Daily Country Intelligence Brief';
            subtitle = 'High-Fidelity Strategic Intelligence • Daily Micro Report';
            if (finalReport?.reportArea) {
                title = finalReport.title;
                subtitle = 'Local events and national context';
            }
        }

        return (
            <AnalysisReportShell
                {...persistenceProps}
                title={title}
                toolbarTitle={finalReport?.reportType === INTELLIGENCE_REPORT_TYPE ? 'Intelligence assessment' : undefined}
                subtitle={subtitle}
                report={displayReport}
                onCustomSave={onCustomSave}
                customSaveLabel={customSaveLabel}
                onNavigate={onNavigate}
                onRegenerate={regenerate}
                onBack={onBack}
                backLabel={backLabel}
                readOnly={readOnly || finalReport?.reportType === INTELLIGENCE_REPORT_TYPE}
            >
                <Component 
                    result={displayReport}
                    report={displayReport}
                    alerts={displayAlerts}
                    onNavigate={onNavigate}
                    onStartFsatAudit={onStartFsatAudit}
                    onLaunchChecklist={onLaunchChecklist}
                    onConvert={readOnly ? undefined : onConvertAnalysisToProject}
                    isLoaded={isLoaded}
                    srmProject={srmProject}
                    srmModule={srmModule}
                    onSaveToFramework={readOnly ? undefined : onSaveToFramework}
                    isDailyMicro={isDailyMicro}
                    readOnly={readOnly}
                    onRefreshConfidence={!readOnly && (propReportId || reportId || (result as any)?.reportId)
                        && !(propReportId || reportId || (result as any)?.reportId)?.startsWith('saved-intel:')
                        ? () => void refreshConfidence() : undefined}
                    refreshingConfidence={refreshingConfidence}
                    confidenceRefreshError={confidenceRefreshError}
                />
            </AnalysisReportShell>
        );
    }


    // Fallback: Text Result or Default Generic View
    if (isTextResult(finalReport) || finalReport.summary || finalReport.additionalSections?.length) {
        return (
            <AnalysisReportShell
                {...persistenceProps}
                title="Intelligence Analysis"
                subtitle="Unified Intelligence Report"
                report={finalReport}
                onCustomSave={onCustomSave}
                customSaveLabel={customSaveLabel}
                onNavigate={onNavigate}
                onRegenerate={regenerate}
                onBack={onBack}
                backLabel={backLabel}
                readOnly={readOnly}
            >
                <TextResultContent result={finalReport} />
            </AnalysisReportShell>
        );
    }

    // Final Fallback: Strategic Intelligence Briefing
    return (
        <AnalysisReportShell
            {...persistenceProps}
            title="Strategic Intelligence Briefing"
            subtitle="Security Operations Center Feed"
            report={finalReport}
            onCustomSave={onCustomSave}
            customSaveLabel={customSaveLabel}
            onNavigate={onNavigate}
            onRegenerate={regenerate}
            onBack={onBack}
            backLabel={backLabel}
            readOnly={readOnly}
        >
            <ThreatGroupBriefing result={finalReport} />
        </AnalysisReportShell>
    );
};

export default AnalysisResultDisplay;
